// Ручки рекламы в консоли (этап 8, П1): /admin/ads/*.
//
// Свой маленький клиент раздела, а не общий `api` консоли: реклама живёт
// только на шлюзе (мок-режима нет), а общий интерфейс Api и его мок правят
// все пакеты сразу. Типы — из корневого barrel @osmix/contracts (subpath
// dev-бандлер консоли резолвит по снимку lockfile, см. api/http.ts).
import type {
	AdAttribution,
	AdAttributionState,
	AdCampaign,
	AdCampaignStats,
	AdCampaignStatus,
	AdModel,
	AdPointsBalance,
	AdPointsEntry,
	AdSettings,
	AdSettingsPatch,
	AdSummary,
} from "@osmix/contracts";
import { ApiClientError, createApiClient } from "@osmix/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/errors";
import { getAuthToken } from "@/api/token";

const readEnv = (read: () => string | undefined): string | undefined => {
	try {
		return read();
	} catch {
		return undefined;
	}
};

// Литерал целиком: Bun подставляет BUN_PUBLIC_* при сборке только так.
const GATEWAY_URL = readEnv(() => process.env.BUN_PUBLIC_API_GATEWAY_URL) ?? "";

export const adsAvailable = Boolean(GATEWAY_URL);

const client = GATEWAY_URL ? createApiClient({ baseUrl: GATEWAY_URL, getBearerToken: getAuthToken }) : null;

type Envelope<T> = { success: true; data: T } | { success: false; error?: { code?: string; message?: string } };

const call = async <T>(
	path: string,
	opts: { method?: string; query?: Record<string, string | number | undefined>; body?: unknown } = {},
): Promise<T> => {
	if (!client) throw new ApiError("Реклама доступна только при подключении к шлюзу", 503);
	try {
		const payload = await client.request<Envelope<T>>(path, opts);
		if (payload?.success) return payload.data;
		throw new ApiError(payload?.error?.message ?? "Request failed", 400, payload?.error?.code);
	} catch (error) {
		if (error instanceof ApiError) throw error;
		if (error instanceof ApiClientError) {
			const body = error.body as Envelope<T> | null;
			const err = body && body.success === false ? body.error : undefined;
			throw new ApiError(err?.message ?? `Request failed: ${error.status}`, error.status, err?.code);
		}
		throw error;
	}
};

/** Рекламные баллы продавца в консоли (PDF п.4): баланс, партии и журнал. */
export type AdminAdPoints = AdPointsBalance & { history: AdPointsEntry[] };

export type AdminCampaignFilter = { status?: AdCampaignStatus | "all"; model?: AdModel | ""; q?: string };
export type AdReport = AdSummary & { byCampaign: (AdSummary["byCampaign"][number] & { shopName?: string })[] };

export const adminAds = {
	list: (filter: AdminCampaignFilter) =>
		call<AdCampaign[]>("/admin/ads/campaigns", {
			query: { status: filter.status ?? "all", model: filter.model || undefined, q: filter.q || undefined },
		}),
	get: (id: string) => call<AdCampaign>(`/admin/ads/campaigns/${encodeURIComponent(id)}`),
	stats: (id: string, range: { from?: string; to?: string }) =>
		call<AdCampaignStats>(`/admin/ads/campaigns/${encodeURIComponent(id)}/stats`, { query: range }),
	approve: (id: string) => call<AdCampaign>(`/admin/ads/campaigns/${encodeURIComponent(id)}/approve`, { method: "POST" }),
	reject: (id: string, reason: string) =>
		call<AdCampaign>(`/admin/ads/campaigns/${encodeURIComponent(id)}/reject`, { method: "POST", body: { reason } }),
	settings: () => call<AdSettings>("/admin/ads/settings"),
	updateSettings: (patch: AdSettingsPatch) => call<AdSettings>("/admin/ads/settings", { method: "PATCH", body: patch }),
	report: (range: { from?: string; to?: string }) => call<AdReport>("/admin/ads/report", { query: range }),
	/** userId — владелец магазина (как у «Промокода партнёра»). */
	points: (userId: string) => call<AdminAdPoints>(`/admin/sellers/${encodeURIComponent(userId)}/ad-points`),
	adjustPoints: (userId: string, delta: number, reason: string) =>
		call<AdminAdPoints>(`/admin/sellers/${encodeURIComponent(userId)}/ad-points/adjust`, {
			method: "POST",
			body: { delta, reason },
		}),
	attributions: (filter: { orderId?: string; campaignId?: string; state?: AdAttributionState | "" }) =>
		call<AdAttribution[]>("/admin/ads/attributions", {
			query: { orderId: filter.orderId || undefined, campaignId: filter.campaignId || undefined, state: filter.state || undefined },
		}),
};

const KEY = ["admin-ads"] as const;

export const useAdminCampaigns = (filter: AdminCampaignFilter) =>
	useQuery({
		queryKey: [...KEY, "list", filter.status ?? "all", filter.model ?? "", filter.q ?? ""],
		queryFn: () => adminAds.list(filter),
		enabled: adsAvailable,
	});

export const useAdminCampaign = (id: string) =>
	useQuery({ queryKey: [...KEY, "one", id], queryFn: () => adminAds.get(id), enabled: adsAvailable && Boolean(id) });

export const useAdminCampaignStats = (id: string, range: { from?: string; to?: string }) =>
	useQuery({
		queryKey: [...KEY, "stats", id, range.from, range.to],
		queryFn: () => adminAds.stats(id, range),
		enabled: adsAvailable && Boolean(id),
	});

export const useAdSettingsAdmin = () =>
	useQuery({ queryKey: [...KEY, "settings"], queryFn: adminAds.settings, enabled: adsAvailable });

export const useAdReport = (range: { from?: string; to?: string }) =>
	useQuery({ queryKey: [...KEY, "report", range.from, range.to], queryFn: () => adminAds.report(range), enabled: adsAvailable });

export const useAdAttributions = (filter: { orderId?: string; campaignId?: string; state?: AdAttributionState | "" }) =>
	useQuery({
		queryKey: [...KEY, "attributions", filter.orderId ?? "", filter.campaignId ?? "", filter.state ?? ""],
		queryFn: () => adminAds.attributions(filter),
		enabled: adsAvailable,
	});

export const useAdModeration = () => {
	const queryClient = useQueryClient();
	const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
	return {
		approve: useMutation({ mutationFn: (id: string) => adminAds.approve(id), onSuccess: refresh }),
		reject: useMutation({
			mutationFn: ({ id, reason }: { id: string; reason: string }) => adminAds.reject(id, reason),
			onSuccess: refresh,
		}),
		saveSettings: useMutation({ mutationFn: (patch: AdSettingsPatch) => adminAds.updateSettings(patch), onSuccess: refresh }),
	};
};

// ── Подписи ────────────────────────────────────────────────────────────────

export const STATUS_LABEL: Record<AdCampaignStatus, string> = {
	draft: "Черновик",
	pending_moderation: "На модерации",
	approved: "Одобрена",
	rejected: "Отклонена",
	active: "Идёт",
	paused: "На паузе",
	completed: "Завершена",
};

export const PACKAGE_LABEL = { basic: "Базовый", extended: "Расширенный", maximum: "Максимальный охват" } as const;

export const STATE_LABEL: Record<AdAttributionState, string> = {
	pending: "Ждёт доставки",
	held: "Холд после доставки",
	charged: "Удержано",
	reversed: "Отменено",
};

export const REVERSE_LABEL: Record<string, string> = {
	order_cancelled: "заказ отменён",
	returned: "возврат до удержания",
	returned_after_charge: "возврат после удержания",
};

const rubFormat = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });
export const rub = (value: number) => `${rubFormat.format(value)} руб.`;
export const pct = (value: number | null, digits = 1) =>
	value == null ? "-" : `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: digits }).format(value * 100)} %`;
export const dateTime = (iso: string | null) =>
	iso ? new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" }) : "-";
export const dateOnly = (iso: string) => new Date(iso).toLocaleDateString("ru-RU");

export const modelLabel = (c: Pick<AdCampaign, "model" | "package">) =>
	c.model === "cpa" && c.package ? `CPA · ${PACKAGE_LABEL[c.package]}` : "CPM";

export const rateLabel = (c: Pick<AdCampaign, "model" | "rate">) =>
	c.model === "cpa" ? pct(c.rate) : `${rub(c.rate)} за 1000 показов`;

/** Таймер SLA: «осталось 12 ч» / «просрочено на 3 ч». */
export const slaLabel = (dueIso: string | null, now = Date.now()): { text: string; overdue: boolean } => {
	if (!dueIso) return { text: "-", overdue: false };
	const diff = new Date(dueIso).getTime() - now;
	const hours = Math.round(Math.abs(diff) / 3_600_000);
	if (diff >= 0) return { text: hours < 1 ? "меньше часа" : `осталось ${hours} ч`, overdue: false };
	return { text: `просрочено на ${Math.max(1, hours)} ч`, overdue: true };
};

/** Окно отчёта по Москве: последние N дней включая сегодня. */
export const lastDays = (days: number): { from: string; to: string } => {
	const msk = (offset: number) => new Date(Date.now() + 3 * 3_600_000 - offset * 86_400_000).toISOString().slice(0, 10);
	return { from: msk(days - 1), to: msk(0) };
};
